import { useMemo, useState } from "react";
import { StyleSheet, View, type LayoutChangeEvent } from "react-native";
import { useTranslation } from "react-i18next";

import { pictureScaleOf } from "../pictureScale";
import type { UiTheme } from "../theme";
import { annotateChartSeries, resolveHeadlineIndex, type ChartSeries } from "./chartAnnotations";
import { buildChartA11ySummary } from "./chartA11ySummary";
import { ChartTapLayer, MARKER_EXTENT } from "./ChartTapLayer";
import { plotA11yProps, type PointSelection } from "./pointSelection";
import { WagmiPathLayers } from "./WagmiPathLayers";
import { WagmiChartA11yPoints } from "./WagmiChartA11yPoints";
import { WagmiChartDots } from "./WagmiChartDots";
import {
  buildWagmiChartModel,
  decimateBuiltSeries,
  wagmiAxisRange,
  wagmiPixelMapper,
  WAGMI_Y_GUTTER,
} from "./wagmiChartModel";
import { WagmiChartLegend } from "./WagmiChartLegend";

export type { ChartSeries, ChartVariant } from "./chartAnnotations";
export { lineColor } from "./chartDots";

// Interactive line chart shared across the measurement / metabolic trends and
// the Progress panels (S3 lift + Progress follow-up, 2026-06-29). Renderer
// swapped from our own SVG (`LineChart.tsx`) to `react-native-wagmi-charts`
// (PG-1, 2026-09-17) once Antenore confirmed it renders AND takes the mouse
// cursor/tooltip on web (`WagmiSpikeChart.tsx` was the spike that proved it).
// The freshness/annotation logic lives in `chartAnnotations.ts`, pure and
// renderer-agnostic, and this file only draws it. ⛔ That module used to CUT
// the line at every silence and stopped on 2026-09-18 (PG-36): ONE instance
// per SERIES now, not one per piece of curve. Why, and what replaced the cut,
// is written out there, read it before reinstating anything.
//
// Design (Antenore): lines are NEUTRAL, brand orange is reserved for the
// FOCAL e1RM series (its own `focal` variant), except the latest point of a
// neutral primary series, which stays orange as the "now" accent. A secondary
// curve (e.g. a Health-Connect-imported value) is a muted dashed line.
//
// Variants:
//  - primary   solid `ink` line, last dot `brand500` ("now")
//  - focal     solid `brand500` line (the e1RM exception), colour-budget KEEP
//  - secondary muted dashed `ink3` line (no "now" dot)
//
// This file is the orchestrator: the model (`lo`/`hi`/`x0`/`xEnd`/`data`/
// `layers`) is `wagmiChartModel.ts`, pure and React-free. PG-42's real-sample
// downsampling (`decimateBuiltSeries`) plugs in HERE, right below, AFTER
// `annotateChartSeries` runs, not before: see the comment on `built` for why.
// The dots overlay is `WagmiChartDots.tsx`, the non-visual a11y sibling is
// `WagmiChartA11yPoints.tsx`, the wagmi tree is `WagmiPathLayers.tsx`, the
// tap that reads the chart is `ChartTapLayer.tsx` (PG-64 fetta 3).
//
// 🪤 wagmi doesn't know its own container: its default `width` is
// `Dimensions.get('window')`, the SCREEN. Measure with `onLayout` and don't
// mount until it's >0 (`WagmiSpikeChart.tsx:36-45`).
// 🪤 wagmi's `<LineChart.Path>` defaults `color` to `'black'`: every colour
// here comes from the theme, never wagmi's default.
// 🪤 `<LineChart>` (Chart.tsx) reserves a FIXED 40px at the bottom of its own
// `height` for a cursor x-axis label, not configurable via props. At the
// compact heights these rows use (48–80px) that would eat most of the
// drawing area, so each layer is given `height + WAGMI_BOTTOM_RESERVED` and
// clipped back down to `height`, the drawing band then matches the
// `height` prop this component was asked for.
// The plot box clips at `height` too: nothing this component draws leaves
// its own box.
// 🪤 `<LineChart.Provider>` shrinks a path's width when its OWN dataset is
// shorter than the Provider's default `xLength` (streaming-placeholder
// behaviour we don't want, every series must reach the full measured
// width). `xLength={1}` disables it unconditionally (every series holds
// ≥1 point, so the `xLength > data.length` check this guards is never true).
// 🪤 wagmi's `<LineChart>` defaults `shape` to `d3Shape.curveBumpX` (a smoothed
// curve). Every series here has 3–11 points, so a smoothed curve draws values
// that never existed between two real samples, the same reason the reps-at-
// load axis uses bars instead of a line (PG-4, `TierOneTrendRow.tsx`). Passed
// `curveLinear` explicitly on every `<Wagmi>` mount: straight segments only.
//
// PG-64 fetta 3 (Antenore, 02/10): the chart is read by a TAP, like the bars
// of PG-63, never by a hold: the host passes a `selection` and its header
// quotes the picked point. The
// per-point accessibilityLabel stays, see `WagmiChartA11yPoints.tsx`. The
// plot's own label below is likewise not gated on `showMeta`, and says more
// than the range: the latest value and the time window too.

type Props = {
  series: ChartSeries[];
  theme: UiTheme;
  yFormat?: (y: number) => string;
  height?: number;
  /** Show the range + per-series current-value footer. Off for compact
   *  per-row sparklines where the current value is already shown beside the
   *  chart (e.g. the E1RM trends panel). Default on. */
  showMeta?: boolean;
  /** Right edge of the time axis. The axis runs [oldest point → now], so a
   *  series that stopped days ago visibly ends short of the edge instead of
   *  re-stretching over its own silence. Defaults to render time; hosts that
   *  already hold a clock (MetabolicCard) pass it so the value is stable. */
  nowMs?: number;
  /** A tap reads the chart: indexes the headline series' points as passed.
   *  The host owns it, its header quotes the pick. Absent: not tappable. */
  selection?: PointSelection;
};

export function WagmiLineChart({
  series,
  theme,
  yFormat = (n) => n.toFixed(0),
  height: pointHeight = 80,
  showMeta = true,
  nowMs,
  selection,
}: Props) {
  // In a picture drawn at k px per point, the plot keeps its proportions.
  // It draws inside a margin as wide as the selection ring, so neither the
  // ring nor a dot on the first or last point is cut.
  const k = pictureScaleOf(theme);
  const height = pointHeight * k;
  const yGutter = WAGMI_Y_GUTTER * k;
  const inset = MARKER_EXTENT * k;
  const { t, i18n } = useTranslation();
  const [plotWidth, setPlotWidth] = useState(0);
  const onLayout = (e: LayoutChangeEvent) => setPlotWidth(e.nativeEvent.layout.width);
  const width = Math.max(0, plotWidth - 2 * inset);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }),
    [i18n.language],
  );

  const now = nowMs ?? Date.now();

  // Annotate the RAW series first, exactly as before PG-42 existed:
  // `cadenceMs`/`freshness`/`isGap` (`chartAnnotations.ts:118-160`) must read
  // the gap between REAL neighbouring samples, or cadence inflates and
  // `gapDays` gets invented inside a wide bucket (24/09 review: decimating
  // before this used to feed annotation the KEPT points instead).
  const built = useMemo(() => annotateChartSeries(series, now), [series, now]);

  // PG-42's real-sample downsampling runs AFTER annotation instead, on
  // `built`: one set of kept indices per series, applied to `points` and
  // `dots` together (`decimateBuiltSeries`, `wagmiChartModel.ts`), so every
  // annotation stays attached to the sample it was computed from. The axis
  // is the SAME one `buildWagmiChartModel` derives from below (`wagmiAxisRange`,
  // called ONCE here, never a second copy or a second call): columns are
  // pixel columns of the shared `[x0, xEnd]` axis, so two series' buckets
  // line up. `width === 0` (before `onLayout`) or `columns <= 0`: passthrough.
  // `keptIdx` (per series key, kept position -> raw point index) maps a tap
  // on a drawn point back to the index the selection holds.
  const axis = useMemo(() => wagmiAxisRange(series, now), [series, now]);
  const { built: decimatedBuilt, keptIdx } = useMemo(
    () => decimateBuiltSeries(built, { x0: axis.x0, xEnd: axis.xEnd, columns: Math.floor(width) }),
    [built, axis, width],
  );

  // PG-31/PG-33, the headline series (the tap's reading + a11y "latest"
  // line) is DECLARED (`ChartSeries.headline`), never deduced from array
  // order, see `resolveHeadlineIndex` in `chartAnnotations.ts`.
  const headlineIndex = useMemo(() => resolveHeadlineIndex(decimatedBuilt), [decimatedBuilt]);
  const headlineKey = decimatedBuilt[headlineIndex]?.key;

  // PG-46/PG-48 review round 4, finding #5, the "nothing to draw" bail was
  // an early `return null` right here, BEFORE the model below existed as a
  // hook. A `useMemo` call after a conditional `return` is a
  // `react-hooks/rules-of-hooks` violation (hooks must run in the SAME ORDER
  // every render), caught by `pnpm lint`, not by any test. `isEmpty` is read
  // out of the model below and the actual `return null` sits AFTER every hook
  // call, right before the JSX return.
  // Deps `[series, decimatedBuilt, axis]`: `decimatedBuilt` is itself
  // already memoized on `[built, axis, width]` above, which is itself
  // memoized on `[series, now]`, so it only gets a NEW reference when one of
  // those actually changed, meaning `data`/`layers` inside this model only
  // change together with `decimatedBuilt`, never on a selection-only
  // re-render: `<Wagmi.Provider data={data}>` has no way to tell "same
  // series, new reference" from "the data changed". `series` (raw, not
  // `decimatedBuilt`) is what `lo`/`hi` are computed from; `axis` (the SAME one computed above, passed straight
  // through, 25/09 review) is what `x0`/`xEnd` are, one call to
  // `wagmiAxisRange` per render, not two.
  const { isEmpty, lo, hi, x0, xEnd, data, layers } = useMemo(
    () => buildWagmiChartModel(series, decimatedBuilt, axis),
    [series, decimatedBuilt, axis],
  );

  // The container's own accessible summary, announced once, unconditional
  // on `showMeta` (a screen reader gets this even on a compact sparkline row
  // that shows no meta footer visually). Says more than the bare lo–hi range:
  // the latest value of the DECLARED headline series (`headlineIndex` above).
  // PG-56 review: the two `t()` calls that build it moved to
  // `chartA11ySummary.ts`, shared with `WagmiSpikeChart.tsx` rather than
  // copied there.
  const summaryLabel = buildChartA11ySummary({
    t,
    dateFmt,
    lo,
    hi,
    x0,
    xEnd,
    last: decimatedBuilt[headlineIndex]?.last,
    yFormat,
  });

  // The actual bail, now that every hook above has run unconditionally (see
  // the comment above the model's `useMemo`).
  if (isEmpty) return null;

  // The tap reads the headline series: every drawn point of it is a target,
  // mapped back to its raw index, and the ring sits on the raw point picked.
  const { pxX, pxY } = wagmiPixelMapper({ x0, xEnd, lo, hi }, width, height, yGutter);
  const headlinePoints = built[headlineIndex]?.points ?? [];
  const targets = (headlineKey === undefined ? [] : (keptIdx[headlineKey] ?? [])).flatMap((i) => {
    const p = headlinePoints[i];
    return p ? [{ i, x: pxX(p.x) }] : [];
  });
  const picked = selection ? headlinePoints[selection.selected] : undefined;

  return (
    <View style={styles.wrap}>
      <View
        style={[styles.plot, { height }]}
        onLayout={onLayout}
        {...plotA11yProps(summaryLabel, selection)}
      >
        {width === 0 || layers.length === 0 ? null : (
          <WagmiPathLayers
            data={data}
            x0={x0}
            xEnd={xEnd}
            lo={lo}
            hi={hi}
            layers={layers}
            theme={theme}
            width={width}
            inset={inset}
            height={height}
            yGutter={yGutter}
          />
        )}

        <WagmiChartDots
          layers={layers}
          data={data}
          theme={theme}
          x0={x0}
          xEnd={xEnd}
          lo={lo}
          hi={hi}
          width={width}
          inset={inset}
          yGutter={yGutter}
          height={height}
        />

        {selection && width > 0 ? (
          <ChartTapLayer
            theme={theme}
            targets={targets}
            marker={picked ? { x: pxX(picked.x), y: pxY(picked.y) } : undefined}
            selection={selection}
            width={width}
            height={height}
            left={inset}
          />
        ) : null}
      </View>

      {/* Non-visual sibling of the plot: the tap layer is hidden from the
          reader, so each point gets its OWN accessible element here, a
          separate sibling, never a descendant of `plot`, which is `accessible`
          and would otherwise collapse them all into its one summary label. */}
      <WagmiChartA11yPoints built={decimatedBuilt} yFormat={yFormat} />

      {showMeta ? (
        <WagmiChartLegend built={decimatedBuilt} theme={theme} lo={lo} hi={hi} yFormat={yFormat} />
      ) : null}
    </View>
  );
}

// Neither style below reads the theme (the footer that used to, `meta`/
// `range`/`currents`/`current`, moved to `WagmiChartLegend.tsx`), so this is
// a plain constant now, not a per-theme `makeStyles`.
const styles = StyleSheet.create({
  wrap: { width: "100%" },
  plot: { width: "100%", position: "relative", overflow: "hidden" },
});
